import { parseISO } from 'date-fns';
import { sortBy } from 'es-toolkit';
import { AwardNominationSummary, ScrubbingStatusId } from '../../models';
import { compareDates } from '../date/date-compare';
import { getUserNames } from '../string/get-user-names';
import { compareStrings } from '../string/string-compare';
import { getRoleRank } from './get-role-rank';
import { NominationSummarySortKey } from './nomination-summary-sort-key';

export const sortAwardNominationSummariesByKey = (awardNominationSummaries: AwardNominationSummary[], key: NominationSummarySortKey, isDescending: boolean): AwardNominationSummary[] => {
  if (key === 'role') {
    const sorted = sortBy(awardNominationSummaries, [(summary) => getRoleRank(summary.role)]);
    return isDescending ? sorted.reverse() : sorted;
  }

  if (key === 'score') {
    return sortBy(awardNominationSummaries, [
      (summary) => getRoleRank(summary.role),
      (summary) => isDescending ? -(summary.score ?? 0) : (summary.score ?? 0),
    ]);
  }

  return awardNominationSummaries
    .slice()
    .sort((awardNominationSummaryA, awardNominationSummaryB) => {
      let result = 0;
      switch (key) {
        case 'title':
          result = compareStrings(awardNominationSummaryA.title, awardNominationSummaryB.title);
          break;
        case 'alias':
          result = compareStrings(awardNominationSummaryA.alias, awardNominationSummaryB.alias);
          break;
        case 'last-edited':
          result = compareDates(
            awardNominationSummaryA.lastEditedDate ? parseISO(awardNominationSummaryA.lastEditedDate) : undefined,
            awardNominationSummaryB.lastEditedDate ? parseISO(awardNominationSummaryB.lastEditedDate) : undefined);
          break;
        case 'scrubbing-status':
          result = compareStrings(
            awardNominationSummaryA.scrubbingStatusDisplayName ? awardNominationSummaryA.scrubbingStatusDisplayName : undefined,
            awardNominationSummaryB.scrubbingStatusDisplayName ? awardNominationSummaryB.scrubbingStatusDisplayName : undefined,
          );
          break;
        case 'scrubbing-by':
          result = compareStrings(
            awardNominationSummaryA.scrubbedBy ? awardNominationSummaryA.scrubbedBy : undefined,
            awardNominationSummaryB.scrubbedBy ? awardNominationSummaryB.scrubbedBy : undefined,
          );
          break;
        case 'scrubbing-by-assigned-date':
          result = compareDates(
            awardNominationSummaryA.scrubbedByAssignedDate ? parseISO(awardNominationSummaryA.scrubbedByAssignedDate) : undefined,
            awardNominationSummaryB.scrubbedByAssignedDate ? parseISO(awardNominationSummaryB.scrubbedByAssignedDate) : undefined
          );
          break;
        case 'scrubbing-source': {
          const autoSourceCode = (summary: AwardNominationSummary): string | undefined =>
            (summary.scrubbingStatusId === ScrubbingStatusId.Disqualified
              || summary.scrubbingStatusId === ScrubbingStatusId['Pending-Disqualified'])
              && (summary.scrubbingSourceCode === 'Rule' || summary.scrubbingSourceCode === 'LLM')
              ? summary.scrubbingSourceCode
              : undefined;
          result = compareStrings(autoSourceCode(awardNominationSummaryA), autoSourceCode(awardNominationSummaryB));
          break;
        }
        case 'ai-score':
          result = (awardNominationSummaryA.aiOverallScore ?? -1) - (awardNominationSummaryB.aiOverallScore ?? -1);
          break;
        case 'status':
          result = compareStrings(
            awardNominationSummaryA.scrubbingStatusId > 0 ? awardNominationSummaryA.scrubbingStatusDisplayName : awardNominationSummaryA.statusDisplayName,
            awardNominationSummaryB.scrubbingStatusId > 0 ? awardNominationSummaryB.scrubbingStatusDisplayName : awardNominationSummaryB.statusDisplayName
          );
          break;
        case 'assigned-to':
          result = compareStrings(
            awardNominationSummaryA.judges && awardNominationSummaryA.judges.length > 0 ? getUserNames(awardNominationSummaryA.judges.map(judge => judge.user)) : '',
            awardNominationSummaryB.judges && awardNominationSummaryB.judges.length > 0 ? getUserNames(awardNominationSummaryB.judges.map(judge => judge.user)) : ''
          );
          break;
        case 'assigned-date':
          result = compareDates(
            awardNominationSummaryA.judges && awardNominationSummaryA.judges.length > 0 ? parseISO(awardNominationSummaryA.judges[0].assignmentDate!) : undefined,
            awardNominationSummaryB.judges && awardNominationSummaryB.judges.length > 0 ? parseISO(awardNominationSummaryB.judges[0].assignmentDate!) : undefined
          );
          break;
        case 'judged':
          if (awardNominationSummaryA.numberOfCompletedJudgment > awardNominationSummaryB.numberOfCompletedJudgment) result = 1;
          else if (awardNominationSummaryA.numberOfCompletedJudgment < awardNominationSummaryB.numberOfCompletedJudgment) result = -1;
          else result = 0;
          break;
        case 'company':
          result = compareStrings(awardNominationSummaryA.company, awardNominationSummaryB.company);
          break;
        case 'questionnaire-last-updated-date':
          result = compareDates(
            awardNominationSummaryA.questionnaireSummaries && awardNominationSummaryA.questionnaireSummaries.length > 0 && awardNominationSummaryA.questionnaireSummaries[0].lastUpdatedDate
              ? parseISO(awardNominationSummaryA.questionnaireSummaries[0].lastUpdatedDate)
              : undefined,
            awardNominationSummaryB.questionnaireSummaries && awardNominationSummaryB.questionnaireSummaries.length > 0 && awardNominationSummaryB.questionnaireSummaries[0].lastUpdatedDate
              ? parseISO(awardNominationSummaryB.questionnaireSummaries[0].lastUpdatedDate)
              : undefined);
          break;
        case 'questionnaire-status':
          result = compareStrings(
            awardNominationSummaryA.questionnaireSummaries && awardNominationSummaryA.questionnaireSummaries.length > 0 && awardNominationSummaryA.questionnaireSummaries[0].status ?
              awardNominationSummaryA.questionnaireSummaries[0].status : undefined,
            awardNominationSummaryB.questionnaireSummaries && awardNominationSummaryB.questionnaireSummaries.length > 0 && awardNominationSummaryB.questionnaireSummaries[0].status ?
              awardNominationSummaryB.questionnaireSummaries[0].status : undefined
          );
          break;
        default:
          return 0;
      }

      return isDescending ? result * -1 : result;
    });
}
